// utils/errorHandler.jsx - Gestion globale des erreurs avec messages utilisateur-friendly
import React from 'react';
import toast from 'react-hot-toast';

/**
 * Détecte si l'utilisateur est en ligne
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Vérifie si c'est une erreur de connexion réseau
 */
export const isNetworkError = (error) => {
  if (!isOnline()) return true;
  
  const errorMessage = error?.message?.toLowerCase() || '';
  const errorName = error?.name?.toLowerCase() || '';
  
  return (
    errorName === 'networkerror' ||
    errorName === 'typeerror' ||
    errorMessage.includes('failed to fetch') ||
    errorMessage.includes('network request failed') ||
    errorMessage.includes('networkerror') ||
    errorMessage.includes('erreur de connexion au serveur') ||
    errorMessage.includes('connection') ||
    errorMessage.includes('network') ||
    errorMessage.includes('fetch')
  );
};

/**
 * Vérifie si c'est une erreur de timeout
 */
const isTimeoutError = (error) => {
  const errorMessage = error?.message?.toLowerCase() || '';
  return errorMessage.includes('timeout') || errorMessage.includes('délai');
};

/**
 * Vérifie si c'est une erreur d'authentification (401)
 */
const isAuthError = (status) => {
  return status === 401;
};

/**
 * Vérifie si c'est une erreur de permission (403)
 */
const isPermissionError = (error) => {
  if (error?.response?.status === 403 || error?.status === 403) {
    const message = extractApiErrorMessage(error) || '';
    // Si le message mentionne "rôle" ou "permission", c'est une erreur de permission
    return message.includes('rôle') || 
           message.includes('Rôle') || 
           message.includes('permission') ||
           message.includes('Permission') ||
           message.includes('requis') ||
           message.includes('Requis') ||
           message.includes('Access denied') ||
           message.includes('Forbidden');
  }
  return false;
};

/**
 * Extrait le message d'erreur personnalisé depuis la réponse API
 */
const extractApiErrorMessage = (error) => {
  // Essayer de récupérer le message depuis différents emplacements
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  if (error?.data?.message) {
    return error.data.message;
  }
  if (error?.data?.error) {
    return error.data.error;
  }
  if (error?.message) {
    return error.message;
  }
  return null;
};

/**
 * Mappe les erreurs techniques vers des messages utilisateur-friendly
 */
const getFriendlyErrorMessage = (error, status = null) => {
  // Vérifier d'abord la connexion
  if (!isOnline()) {
    return {
      title: null, // Pas de titre pour un seul message
      message: 'Votre connexion Internet ou Wi‑Fi est inactive. Veuillez l\'activer. Secura ne consomme pas beaucoup de données.',
      duration: 6000
    };
  }

  // Erreurs réseau
  if (isNetworkError(error)) {
    return {
      title: null, // Pas de titre pour un seul message
      message: 'Votre connexion Internet ou Wi‑Fi est inactive. Veuillez l\'activer. Secura ne consomme pas beaucoup de données.',
      duration: 6000
    };
  }

  // Erreurs de timeout
  if (isTimeoutError(error)) {
    return {
      title: 'Délai dépassé',
      message: 'La requête a pris trop de temps. Vérifiez votre connexion Internet et réessayez.',
      duration: 4000
    };
  }

  // Erreurs d'authentification (401 uniquement)
  if (status === 401) {
    return {
      title: 'Session expirée',
      message: 'Votre session a expiré. Veuillez vous reconnecter.',
      duration: 4000
    };
  }

  // Erreurs de permission (403) - Ces erreurs sont normales et ne doivent pas alarmer l'utilisateur
  // Elles sont généralement silencieuses ou avec un message informatif
  if (status === 403 && isPermissionError(error)) {
    // Ne pas afficher de toast pour les erreurs de permission
    // Elles sont gérées par le système de routing
    return null;
  }

  // Erreur 400 - Bad Request avec message personnalisé
  if (status === 400) {
    const apiMessage = extractApiErrorMessage(error);
    if (apiMessage) {
      // Messages spécifiques pour les congés
      const lowerMessage = apiMessage.toLowerCase();
      if (lowerMessage.includes('chevauche') || lowerMessage.includes('chevauch')) {
        return {
          title: 'Période déjà réservée',
          message: 'Cette période chevauche une demande de congé existante. Veuillez choisir une autre période.',
          duration: 5000
        };
      }
      if (lowerMessage.includes('soldes insuffisants') || lowerMessage.includes('solde insuffisant')) {
        return {
          title: 'Solde insuffisant',
          message: 'Vous n\'avez pas assez de jours de congé disponibles pour cette période.',
          duration: 5000
        };
      }
      if (lowerMessage.includes('date passée') || lowerMessage.includes('passé')) {
        return {
          title: 'Date invalide',
          message: 'Vous ne pouvez pas créer une demande de congé pour une date passée.',
          duration: 4000
        };
      }
      if (lowerMessage.includes('date fin') || lowerMessage.includes('date de fin')) {
        return {
          title: 'Date invalide',
          message: 'La date de fin doit être postérieure à la date de début.',
          duration: 4000
        };
      }
      
      // Retourner le message de l'API si c'est déjà un message compréhensible
      return {
        title: 'Erreur de validation',
        message: apiMessage,
        duration: 5000
      };
    }
    return {
      title: 'Données invalides',
      message: 'Les informations saisies ne sont pas valides. Vérifiez vos données et réessayez.',
      duration: 4000
    };
  }

  // Erreur 404
  if (status === 404) {
    return {
      title: 'Ressource introuvable',
      message: 'La ressource demandée n\'a pas été trouvée.',
      duration: 4000
    };
  }

  // Erreur 422 - Unprocessable Entity
  if (status === 422) {
    const apiMessage = extractApiErrorMessage(error);
    return {
      title: 'Données invalides',
      message: apiMessage || 'Les données envoyées ne sont pas valides. Veuillez vérifier vos informations.',
      duration: 5000
    };
  }

  // Erreur 429 - Too Many Requests
  if (status === 429) {
    return {
      title: 'Trop de requêtes',
      message: 'Vous avez effectué trop de requêtes. Veuillez patienter quelques instants avant de réessayer.',
      duration: 4000
    };
  }

  // Erreur 500 - Server Error
  if (status === 500) {
    return {
      title: 'Erreur serveur',
      message: 'Une erreur s\'est produite sur le serveur. Veuillez réessayer dans quelques instants.',
      duration: 4000
    };
  }

  // Erreur 502, 503, 504 - Server unavailable
  if (status === 502 || status === 503 || status === 504) {
    return {
      title: 'Service indisponible',
      message: 'Le service est temporairement indisponible. Veuillez réessayer dans quelques instants.',
      duration: 4000
    };
  }

  // Erreur générique avec message de l'API si disponible
  const apiMessage = extractApiErrorMessage(error);
  if (apiMessage && !apiMessage.includes('API') && !apiMessage.includes('Error')) {
    return {
      title: 'Erreur',
      message: apiMessage,
      duration: 5000
    };
  }

  // Message par défaut
  return {
    title: 'Une erreur est survenue',
    message: 'Une erreur inattendue s\'est produite. Veuillez réessayer ou contacter le support si le problème persiste.',
    duration: 4000
  };
};

/**
 * Affiche un toast d'erreur avec un message utilisateur-friendly
 */
export const showErrorToast = (error, status = null) => {
  const errorInfo = getFriendlyErrorMessage(error, status);
  
  // Si errorInfo est null, ne pas afficher de toast (erreur silencieuse)
  if (!errorInfo) {
    return;
  }
  
  // Ne pas afficher de toast si le message contient "Route non trouvée" et qu'on reçoit du HTML
  // (cela signifie que l'API n'est pas accessible et on reçoit la page HTML par défaut)
  if (errorInfo.message && errorInfo.message.includes('Route non trouvée')) {
    console.warn('⚠️ Route API non accessible - Réponse HTML reçue');
    return;
  }
  
  // Ne pas afficher le toast 404 pour certaines routes spécifiques
  if (status === 404) {
    const errorUrl = error?.config?.url || error?.request?.responseURL || error?.config?.baseURL + error?.config?.url || '';
    const errorMessage = error?.response?.data?.message || error?.message || '';
    const routesToIgnore = [
      '/invitation/validate-token',
      '/auth/me',
      '/user/data'
    ];
    
    // Vérifier si l'URL contient une des routes à ignorer
    const shouldIgnore = routesToIgnore.some(route => errorUrl.includes(route));
    
    // Vérifier aussi si le message indique "Utilisateur non trouvé" (normal pour les invitations)
    const isUserNotFound = errorMessage.includes('Utilisateur non trouvé') || errorMessage.includes('non trouvé');
    
    if (shouldIgnore || isUserNotFound) {
      console.warn('⚠️ Erreur 404 ignorée pour la route:', errorUrl, '- Message:', errorMessage);
      return;
    }
  }
  
  // Si pas de titre, afficher seulement le message
  const toastContent = errorInfo.title ? (
    (t) => (
      <div>
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>
          {errorInfo.title}
        </div>
        <div style={{ fontSize: '14px' }}>
          {errorInfo.message}
        </div>
      </div>
    )
  ) : errorInfo.message;

  toast.error(
    toastContent,
    {
      duration: errorInfo.duration,
      position: 'top-center',
      id: 'network-error', // ID unique pour éviter les doublons
      style: {
        background: '#fff',
        color: '#333',
        border: '1px solid #fee',
        borderRadius: '8px',
        padding: '16px',
        maxWidth: '400px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }
    }
  );
};

/**
 * Affiche un toast de succès
 */
export const showSuccessToast = (message, title = 'Succès') => {
  toast.success(
    (t) => (
      <div>
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>
          {title}
        </div>
        <div style={{ fontSize: '14px' }}>
          {message}
        </div>
      </div>
    ),
    {
      duration: 3000,
      position: 'top-center',
      style: {
        background: '#fff',
        color: '#333',
        border: '1px solid #dfe',
        borderRadius: '8px',
        padding: '16px',
        maxWidth: '400px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }
    }
  );
};

/**
 * Affiche un toast d'information
 */
export const showInfoToast = (message, title = 'Information') => {
  toast(
    (t) => (
      <div>
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>
          {title}
        </div>
        <div style={{ fontSize: '14px' }}>
          {message}
        </div>
      </div>
    ),
    {
      duration: 4000,
      position: 'top-center',
      icon: 'ℹ️',
      style: {
        background: '#fff',
        color: '#333',
        border: '1px solid #e3f2fd',
        borderRadius: '8px',
        padding: '16px',
        maxWidth: '400px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }
    }
  );
};

/**
 * Gère une erreur de façon globale
 * Utilisé par les fonctions API pour afficher des toasts automatiques
 * @param {Error|Object} error - L'erreur à gérer
 * @param {number|null} status - Le statut HTTP (optionnel, peut être extrait de error)
 */
export const handleApiError = (error, status = null) => {
  // Extraire le status depuis différentes sources si non fourni
  if (status === null) {
    if (error?.response?.status) {
      status = error.response.status;
    } else if (error?.status) {
      status = error.status;
    }
  }

  // Si c'est une erreur de permission (403), ne pas logger ni afficher de toast
  // Ces erreurs sont normales et gérées par le système de routing
  if (isPermissionError(error)) {
    // Logger silencieusement (en mode debug seulement)
    if (process.env.NODE_ENV === 'development') {
      console.log('Erreur de permission (403) - ignorée:', extractApiErrorMessage(error));
    }
    return {
      status,
      message: extractApiErrorMessage(error) || 'Permission refusée',
      isNetworkError: false,
      isAuthError: false,
      isPermissionError: true,
      isTimeoutError: false
    };
  }

  // Vérifier si c'est une erreur réseau - NetworkErrorHandler gère déjà le toast
  const isNetwork = isNetworkError(error) || !isOnline();
  
  // Pour les erreurs réseau, émettre un événement au lieu d'afficher un toast directement
  // NetworkErrorHandler écoutera cet événement et affichera le toast
  if (isNetwork && typeof window !== 'undefined' && window.dispatchEvent) {
    window.dispatchEvent(new CustomEvent('network-error', { 
      detail: { error } 
    }));
    // Ne pas logger ni afficher de toast pour les erreurs réseau
    // Le NetworkErrorHandler s'en charge
    return {
      status,
      message: extractApiErrorMessage(error) || error?.message || 'Erreur réseau',
      isNetworkError: true,
      isAuthError: false,
      isPermissionError: false,
      isTimeoutError: false
    };
  }
  
  // Logger les autres erreurs (non réseau)
  console.error('Erreur API:', error);
  
  // Afficher le toast pour les autres types d'erreurs (non réseau)
  showErrorToast(error, status);
  
  // Retourner un objet avec les informations d'erreur pour gestion ultérieure si nécessaire
  return {
    status,
    message: extractApiErrorMessage(error) || error?.message || 'Erreur inconnue',
    isNetworkError: isNetwork,
    isAuthError: isAuthError(status),
    isPermissionError: false,
    isTimeoutError: isTimeoutError(error)
  };
};

/**
 * Vérifie la connexion (sans afficher de toast)
 */
export const checkConnection = () => {
  return isOnline();
};

// Export par défaut pour compatibilité
export default {
  showErrorToast,
  showSuccessToast,
  showInfoToast,
  handleApiError,
  checkConnection,
  isOnline,
  isNetworkError
};

